import { NumericInput } from '@components/core-ui/form-elements/NumericInput';
import { Select } from '@components/core-ui/form-elements/Select';
import { Button } from '@components/core-ui/form-elements/buttons/Button';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { getApiErrorMessage } from '@helpers/getApiErrorMessage';
import { useAPI } from '@providers/useAPI';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { asrProxyKeys } from 'app/queries/asrProxy/asrProxyKeys';
import { useEffect, useRef, useState } from 'react';
import { AsrProxyService } from '../../api/AsrProxyService';
import { createFetchAsrModelsQueryFn } from '../../api/fetchAsrModels';
import { createFetchAsrSettingsQueryFn } from '../../api/fetchAsrSettings';
import type { AsrSettingsResponse } from '../../api/types';
import { ModelPipelineSettings } from './components/ModelPipelineSettings';
import { DefaultParameters } from './components/DefaultParameters';

const NO_FALLBACK_OPTION = { value: '', label: '— none —' };

export const DefaultsTab = () => {
  const { call } = useAPI();
  const queryClient = useQueryClient();
  const [retryCount, setRetryCount] = useState(0);
  const [retryDelayMs, setRetryDelayMs] = useState(0);
  const [savedRetries, setSavedRetries] = useState({ count: 0, delayMs: 0 });
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const modelsQuery = useQuery({
    queryKey: asrProxyKeys.models(),
    queryFn: createFetchAsrModelsQueryFn(call),
  });
  const settingsQuery = useQuery({
    queryKey: asrProxyKeys.settings(),
    queryFn: createFetchAsrSettingsQueryFn(call),
  });
  const models = modelsQuery.data?.models ?? [];
  const current = modelsQuery.data?.default_model ?? '';
  const selected = current || models[0]?.model_name || '';

  const lastAppliedSettingsRef = useRef<AsrSettingsResponse>();

  useEffect(() => {
    const settings = settingsQuery.data;
    if (!settings || settings === lastAppliedSettingsRef.current) {
      return;
    }
    lastAppliedSettingsRef.current = settings;
    setRetryCount(settings.retry_count);
    setRetryDelayMs(settings.retry_delay_ms);
    setSavedRetries({ count: settings.retry_count, delayMs: settings.retry_delay_ms });
  }, [settingsQuery.data]);

  const cancelSettingsRefetch = () => queryClient.cancelQueries({ queryKey: asrProxyKeys.settings() });

  const applySettingsSlice = async (slice: Partial<AsrSettingsResponse>) => {
    await cancelSettingsRefetch();
    const stored = queryClient.setQueryData<AsrSettingsResponse>(asrProxyKeys.settings(), (settings) =>
      settings ? { ...settings, ...slice } : settings,
    );
    if (stored) {
      lastAppliedSettingsRef.current = stored;
    }
  };

  const refresh = () => {
    setMessage('');
    setError('');
    void modelsQuery.refetch();
    void settingsQuery.refetch();
  };

  const startSave = () => {
    setMessage('');
    setError('');
  };

  const retriesMutation = useMutation({
    mutationFn: () =>
      call(() => AsrProxyService.setRetries(retryCount, retryDelayMs), {
        dontRedirectOnError: true,
        onError: setError,
      }),
    onMutate: cancelSettingsRefetch,
    onSuccess: async (response) => {
      setSavedRetries({ count: response.retry_count, delayMs: response.retry_delay_ms });
      setMessage(`Retry policy: ${response.retry_count} retries, ${response.retry_delay_ms}ms delay`);
      await applySettingsSlice({ retry_count: response.retry_count, retry_delay_ms: response.retry_delay_ms });
    },
  });

  const saveRetries = () => {
    startSave();
    retriesMutation.mutate();
  };

  const reloading = modelsQuery.isFetching || settingsQuery.isFetching;
  const fetchError = getApiErrorMessage(modelsQuery.error ?? settingsQuery.error);
  const displayedError = error || fetchError;

  const modelOptions = models.map((model) => ({
    value: model.model_name,
    label: `${model.model_name} (${model.provider})`,
  }));

  const retriesUnchanged = retryCount === savedRetries.count && retryDelayMs === savedRetries.delayMs;
  return (
    <div className="space-y-5">
      {message || displayedError ? (
        <div
          className={`rounded-[10px] bg-surface px-5 py-3 text-sm shadow-[0_8px_10px_var(--color-shadow)] ${
            displayedError ? 'text-danger' : 'text-foreground-muted'
          }`}
        >
          {displayedError || message}
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
        <section className="rounded-[10px] bg-surface p-5 shadow-[0_8px_10px_var(--color-shadow)]">
          <h2 className="text-lg font-medium text-foreground">Default model</h2>
          <p className="mt-1 text-sm text-foreground-muted">
            Used when a request omits <code className="rounded bg-page px-1.5 py-0.5 text-xs">model</code>. AssemblyAI
            Universal-2 is the only available model.
          </p>
          <div className="mt-3 text-sm">
            Current default:{' '}
            <code className="rounded bg-page px-1.5 py-0.5 text-xs text-foreground">{current || '—'}</code>
          </div>
          <div className="mt-4">
            <Select
              name="default-model"
              label="Default model"
              value={selected}
              disabled
              options={models.length === 0 ? [{ value: '', label: '— no models loaded —' }] : modelOptions}
              onChange={() => undefined}
              className="form-select w-full"
            />
          </div>
          <div className="mt-4 flex gap-2">
            <Button className="!w-auto" disabled>
              Save default
            </Button>
            <OutlinedButton className="!w-auto" disabled={reloading} onClick={refresh}>
              {reloading ? 'Reloading…' : 'Reload'}
            </OutlinedButton>
          </div>
        </section>

        <section className="rounded-[10px] bg-surface p-5 shadow-[0_8px_10px_var(--color-shadow)]">
          <h2 className="text-lg font-medium text-foreground">Retry policy</h2>
          <p className="mt-1 text-sm text-foreground-muted">Retry automatically when a provider call fails.</p>
          <div className="mt-4 flex gap-3">
            <div className="flex-1">
              <NumericInput label="Retries on error" value={retryCount} min={0} onChange={setRetryCount} />
            </div>
            <div className="flex-1">
              <NumericInput
                label="Delay between retries (ms)"
                value={retryDelayMs}
                min={0}
                step={100}
                onChange={setRetryDelayMs}
              />
            </div>
          </div>
          <Button className="mt-4 !w-auto" disabled={retriesUnchanged} onClick={saveRetries}>
            Save retry policy
          </Button>
        </section>

        <section className="rounded-[10px] bg-surface p-5 shadow-[0_8px_10px_var(--color-shadow)]">
          <h2 className="text-lg font-medium text-foreground">Fallback models</h2>
          <p className="mt-1 text-sm text-foreground-muted">
            Additional fallback models are unavailable while Universal-2 is the only configured model.
          </p>
          {([0, 1] as const).map((index) => (
            <div key={index} className="mt-4">
              <Select
                name={`fallback-${index}`}
                label={`Fallback ${index + 1}`}
                value={settingsQuery.data?.fallback_models[index] ?? ''}
                options={[NO_FALLBACK_OPTION, ...modelOptions]}
                disabled
                onChange={() => undefined}
                className="form-select w-full"
              />
            </div>
          ))}
          <Button className="mt-4 !w-auto" disabled>
            Save fallbacks
          </Button>
        </section>

        <ModelPipelineSettings />
      </div>

      <DefaultParameters />
    </div>
  );
};
